import classNames from 'classnames'

import type { ProductHighlightCard } from '../../types/product'

interface HighlightCardsProps {
  cards: ProductHighlightCard[]
  variant?: 'default' | 'mini'
}

const HighlightCards = ({
  cards,
  variant = 'default',
}: HighlightCardsProps) => {
  const hasTwocards = cards.length > 1
  const isMiniVariant = variant === 'mini'
  const defaultCardLayoutClasses = hasTwocards
    ? 'flex-col items-start sm:flex-row p-4 sm:px-8 sm:py-5 max-w-[calc(50%)] sm:items-center'
    : 'flex-row items-center px-8 py-5'

  const iconContainerDefaultClasses = hasTwocards ? 'w-8 sm:w-12' : 'w-12'
  const descriptionGapDefaultClasses = hasTwocards
    ? 'gap-3 sm:gap-2'
    : 'sm:gap-2'

  const titleDefaultClasses = hasTwocards
    ? 'text-[0.875rem] sm:text-[1rem]'
    : 'text-[1rem]'

  const valueDefaultClasses = hasTwocards
    ? 'text-xl sm:text-font-size-8'
    : 'text-font-size-8'

  const strongValueDefaultClasses = hasTwocards
    ? 'text-[1.25rem] sm:text-[2rem]'
    : 'text-[2rem]'

  return (
    <div
      className={classNames(
        'font-inter',
        isMiniVariant
          ? 'grid grid-cols-1 gap-3 restructure-tablet:grid-cols-2'
          : 'flex flex-wrap gap-3'
      )}
    >
      {cards.map((card, index) => {
        const { image, technicalInfoLabel, technicalInfoValue } = card

        return (
          <div
            className={classNames(
              'flex flex-1',
              isMiniVariant
                ? 'min-h-[156px] items-start gap-3 bg-restructure-background-neutral-3 p-5'
                : classNames(
                    'gap-5 border border-gray',
                    defaultCardLayoutClasses
                  )
            )}
            key={`${technicalInfoLabel}-${index}`}
          >
            <div
              className={classNames(
                isMiniVariant
                  ? 'mt-0.5 flex h-[22px] w-[22px] items-center justify-center shrink-0'
                  : classNames(
                      'flex items-center sm:h-full sm:shrink-0',
                      iconContainerDefaultClasses
                    )
              )}
            >
              {image ? (
                <img
                  src={image}
                  alt={technicalInfoLabel}
                  className={classNames({
                    'h-[22px] w-[22px] object-contain': isMiniVariant,
                  })}
                />
              ) : (
                <span className="text-restructure-primary">|</span>
              )}
            </div>
            <dl
              className={classNames(
                'flex flex-col',
                isMiniVariant ? 'gap-2' : descriptionGapDefaultClasses
              )}
            >
              <dt
                className={classNames(
                  'text-secondary font-medium leading-[100%]',
                  isMiniVariant
                    ? 'text-font-size-3 text-restructure-primary'
                    : titleDefaultClasses
                )}
              >
                {technicalInfoLabel}
              </dt>
              <dd
                className={classNames(
                  'text-secondary font-semibold',
                  isMiniVariant
                    ? 'text-font-size-2 font-normal leading-[1.35] text-secondary line-clamp-3'
                    : valueDefaultClasses
                )}
              >
                <strong
                  className={classNames(
                    'technical-info-value text-secondary',
                    isMiniVariant
                      ? 'text-inherit font-inherit leading-inherit'
                      : strongValueDefaultClasses
                  )}
                >
                  {technicalInfoValue}
                </strong>
              </dd>
            </dl>
          </div>
        )
      })}
    </div>
  )
}

export default HighlightCards
